import React from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { Breadcrumbs as MuiBreadcrumbs, Link, Typography, Box } from '@mui/material';
import { NavigateNext as NavigateNextIcon, Home as HomeIcon } from '@mui/icons-material';

const Breadcrumbs = ({ userType }) => {
    const location = useLocation();
    
    // Define breadcrumb mappings
    const breadcrumbMap = {
        '/': 'Home',
        '/teacher-dashboard': 'Teacher Dashboard',
        '/student-dashboard': 'Student Dashboard',
        '/manage-students': 'Manage Students',
        '/students': 'Students',
        '/students/list': 'Student Directory',
        '/students/add': 'Add Student',
        '/students/upload-students': 'Upload Students',
        '/students/upload-homeroom': 'Upload Homeroom',
        '/groups': 'Groups',
        '/groups/add': 'Add Group',
        '/groups/manage': 'Manage Core Groups',
        '/groups/manage-cte': 'Manage CTE Groups',
        '/groups/homeroom': 'Homeroom Groups',
        '/groups/list': 'Group List',
        '/schedules': 'Schedules',
        '/schedules/create-section': 'Section Periods',
        '/schedules/view-sections': 'View Sections',
        '/schedules/block-schedules': 'Block Schedules',
        '/schedules/block-schedules/add': 'Add Block Schedule',
        '/schedules/tool': 'Schedule Tool',
        '/attendance': 'Attendance',
        '/attendance/take': 'Take Attendance',
        '/reports': 'Reports',
        '/reports/v2': 'Advanced Reports',
        '/schedule': 'Student Schedule'
    };

    // Generate breadcrumb items
    const generateBreadcrumbs = () => {
        const pathnames = location.pathname.split('/').filter((x) => x);
        const breadcrumbs = [];

        // Always start with dashboard
        const dashboardPath = userType === 'teacher' ? '/teacher-dashboard' : '/student-dashboard';
        const dashboardTitle = userType === 'teacher' ? 'Teacher Dashboard' : 'Student Dashboard';
        
        breadcrumbs.push({
            path: dashboardPath,
            title: dashboardTitle,
            isHome: true
        });

        // Build path segments
        let currentPath = '';
        pathnames.forEach((pathname, index) => {
            currentPath += `/${pathname}`;
            
            // Skip dashboard paths since we already added them
            if (currentPath === dashboardPath) return;
            
            // Get title from map or use pathname
            let title = breadcrumbMap[currentPath];
            
            // Handle dynamic routes
            if (!title) {
                if (currentPath.includes('/edit-student/')) {
                    title = 'Edit Student';
                } else if (currentPath.includes('/groups/edit/')) {
                    title = 'Edit Group';
                } else if (currentPath.includes('/courses/edit/')) {
                    title = 'Edit Course';
                } else if (currentPath.includes('/attendance/take/')) {
                    title = 'Take Attendance';
                } else if (currentPath.includes('/schedule/')) {
                    title = 'Student Schedule';
                } else if (currentPath.includes('/edit-section-period/')) {
                    title = 'Edit Section Period';
                } else if (currentPath.includes('/schedules/assign-groups/')) {
                    title = 'Assign Groups';
                } else {
                    // Fallback: capitalize and clean up pathname
                    title = pathname.charAt(0).toUpperCase() + pathname.slice(1).replace(/-/g, ' ');
                }
            }
            
            breadcrumbs.push({
                path: currentPath,
                title: title,
                isLast: index === pathnames.length - 1
            });
        });

        return breadcrumbs;
    };

    const breadcrumbItems = generateBreadcrumbs();

    // Don't show breadcrumbs on dashboard pages
    if (breadcrumbItems.length <= 1) {
        return null;
    }

    return (
        <Box sx={{ mb: 2, px: 3, py: 2, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
            <MuiBreadcrumbs 
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
            >
                {breadcrumbItems.map((item, index) => {
                    if (item.isLast) {
                        return (
                            <Typography key={item.path} color="text.primary" sx={{ fontWeight: 600 }}>
                                {item.title}
                            </Typography>
                        );
                    }

                    return (
                        <Link
                            key={item.path}
                            component={RouterLink}
                            to={item.path}
                            underline="hover"
                            color="inherit"
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                '&:hover': {
                                    color: 'primary.main'
                                }
                            }}
                        >
                            {item.isHome && <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />}
                            {item.title}
                        </Link>
                    );
                })}
            </MuiBreadcrumbs>
        </Box>
    );
};

export default Breadcrumbs;
